import React from 'react';
import { motion } from 'motion/react';
import { FileText, CheckCircle2, ShieldAlert, Scale, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <div className="min-h-screen relative">
      <SEO
        title="Terms of Service | Emerge Automations"
        description="Review the Terms of Service for Emerge Automations, covering our AI voice agents, workflow automation, and consulting services for UK and US businesses."
        canonical="https://emergeautomations.com/terms-of-service/"
      />

      <div className="container-custom pt-32 md:pt-40 pb-24 relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <span className="eyebrow mb-6 inline-block">Legal</span>
          <div className="mb-8 h-px bg-white/10" />
          <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.035em] mb-6">
            Terms of Service
          </h1>
          <p className="text-[17px] text-zinc-400 leading-[1.65] mb-16">
            Last Updated: April 8, 2026. These terms govern your use of Emerge Automations' services, including AI voice agents, workflow automation, and consulting.
          </p>

          <div className="space-y-14 prose-emerge">
            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <CheckCircle2 className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                1. Acceptance of Terms
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                By accessing or using our services, you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not access or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <FileText className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                2. Description of Services
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75] mb-3">
                Emerge Automations provides AI-driven business solutions, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[16px] text-zinc-300 leading-[1.75]">
                <li>Implementation of AI Voice Agents and Chatbots.</li>
                <li>Design and deployment of automated workflows (e.g., n8n, Zapier).</li>
                <li>Strategic AI consulting and readiness audits.</li>
                <li>Custom LLM training and knowledge base development.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <ShieldAlert className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                3. AI Limitations &amp; Disclaimers
              </h2>
              <div className="space-y-3 text-[16px] text-zinc-300 leading-[1.75]">
                <p>
                  <strong>Probabilistic Nature:</strong> You acknowledge that AI models are probabilistic and may occasionally produce inaccurate, biased, or incomplete information. Emerge Automations is not responsible for errors generated by AI models.
                </p>
                <p>
                  <strong>Human Oversight:</strong> We strongly recommend human oversight for all AI-generated outputs, especially those involving customer communication, legal documents, or financial transactions.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 flex items-center gap-3 tracking-[-0.01em]">
                <Scale className="text-[color:var(--color-accent)] w-5 h-5" strokeWidth={1.75} />
                4. Intellectual Property
              </h2>
              <div className="space-y-3 text-[16px] text-zinc-300 leading-[1.75]">
                <p>
                  <strong>Client Ownership:</strong> Upon full payment, you own the specific custom configurations and workflows developed for your business.
                </p>
                <p>
                  <strong>Our IP:</strong> Emerge Automations retains ownership of its proprietary methodologies, pre-built templates, and underlying software used to deliver the services.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 tracking-[-0.01em]">
                5. Limitation of Liability
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                To the maximum extent permitted by law, Emerge Automations shall not be liable for any indirect, incidental, special, or consequential damages resulting from the use or inability to use our services, including data loss or business interruption.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 tracking-[-0.01em]">
                6. Termination
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                We reserve the right to terminate or suspend access to our services immediately, without prior notice or liability, for any reason, including breach of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 tracking-[-0.01em]">
                7. Governing Law
              </h2>
              <p className="text-[16px] text-zinc-300 leading-[1.75]">
                These Terms shall be governed and construed in accordance with the laws of the United Kingdom and the United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="p-6 md:p-8 rounded-[var(--radius-lg)] border border-white/10 bg-[color:var(--color-surface-1)]">
              <h2 className="text-lg font-semibold text-white mb-2">Questions?</h2>
              <p className="text-sm text-zinc-400 leading-relaxed">
                If you have questions about these Terms, please contact us at{' '}
                <a href="mailto:emergeautomations@gmail.com" className="text-[color:var(--color-accent)] hover:underline">
                  emergeautomations@gmail.com
                </a>.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
